import { createElement, type ReactNode } from 'react';
import { ANCHOR_LABEL_FIELDS, type SystemEntity } from '@erp/shared-types';
import type { TaskResponse } from '@erp/rpc';
import { Badge } from 'src/components/ui/badge';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from 'src/components/ui/hover-card';
import {
  SystemEntityPreview,
  getEntityIcon,
} from 'src/components/system-entities';
import {
  SystemEntityLink,
  hasSystemEntityLink,
} from 'src/components/system-entities/system-entity-link';
import {
  anchorSummaryLabels,
  formatAnchorEntityTypeLabel,
  withAnchorLabelPrefix,
  shortAnchorEntityId,
} from './anchorSummaryFormat';

/**
 * Anchor-summary column for task-page queues: a compact entity badge for the
 * business record a typed task is anchored to, keyed by `anchorEntityType`.
 *
 * Task pages ask `tasks.list` for each entity type's `ANCHOR_LABEL_FIELDS`
 * (batch-loaded server-side, one query per type), so the badge label needs no
 * per-row fetch; the hover preview loads the entity only when opened.
 * EXTENSION SEAM: register a bespoke renderer per `SystemEntity` below; every
 * type with label fields gets the labelled badge, the rest the fallback.
 */
type AnchorSummaryRenderer = (task: TaskResponse) => ReactNode;

function AnchorBadge({
  entityType,
  entityId,
  label,
  secondary,
}: {
  entityType: SystemEntity;
  entityId: string;
  label: string;
  secondary: string | null;
}) {
  const badge = (
    <Badge variant="outline" className="min-w-0 max-w-full gap-1.5">
      <span className="flex shrink-0 [&>svg]:size-3">
        {createElement(getEntityIcon(entityType))}
      </span>
      <span className="truncate">{label}</span>
    </Badge>
  );
  return (
    <HoverCard openDelay={300} closeDelay={100}>
      <HoverCardTrigger asChild>
        <span className="relative z-10 flex min-w-0 max-w-full items-center gap-1.5">
          {hasSystemEntityLink(entityType) ? (
            <span onClick={(event) => event.stopPropagation()}>
              <SystemEntityLink
                entityType={entityType}
                entityId={entityId}
                className="min-w-0 max-w-full hover:underline"
              >
                {badge}
              </SystemEntityLink>
            </span>
          ) : (
            badge
          )}
          {secondary && (
            <span className="truncate text-[11px] text-muted-foreground">
              {secondary}
            </span>
          )}
        </span>
      </HoverCardTrigger>
      <HoverCardContent className="w-auto p-0" align="start">
        <SystemEntityPreview entityType={entityType} entityId={entityId} />
      </HoverCardContent>
    </HoverCard>
  );
}

function labelledAnchorRenderer(
  labelFields: readonly string[],
): AnchorSummaryRenderer {
  return function renderLabelledAnchor(task) {
    const labels = anchorSummaryLabels(task.anchor, labelFields);
    if (!labels || !task.anchorEntityType || !task.anchorEntityId) {
      return fallbackAnchorSummary(task);
    }
    return (
      <AnchorBadge
        entityType={task.anchorEntityType}
        entityId={task.anchorEntityId}
        label={withAnchorLabelPrefix(task.anchorEntityType, labels.primary)}
        secondary={labels.secondary}
      />
    );
  };
}

const ANCHOR_SUMMARY_RENDERERS: Partial<
  Record<SystemEntity, AnchorSummaryRenderer>
> = Object.fromEntries(
  Object.entries(ANCHOR_LABEL_FIELDS).map(([entityType, fields]) => [
    entityType,
    labelledAnchorRenderer(fields),
  ]),
);

function fallbackAnchorSummary(task: TaskResponse): ReactNode {
  if (!task.anchorEntityType || !task.anchorEntityId) return null;
  return (
    <span className="flex items-center gap-1.5">
      <span className="rounded bg-stone-100 px-1.5 py-px text-[11px] text-stone-700">
        {formatAnchorEntityTypeLabel(task.anchorEntityType)}
      </span>
      <span className="font-mono text-[11px] tabular-nums text-stone-500">
        {shortAnchorEntityId(task.anchorEntityId)}
      </span>
    </span>
  );
}

export function renderAnchorSummaryCell(task: TaskResponse): ReactNode {
  if (!task.anchorEntityType) return null;
  const render = ANCHOR_SUMMARY_RENDERERS[task.anchorEntityType];
  return render ? render(task) : fallbackAnchorSummary(task);
}
